import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  DestroyRef,
  OnInit,
  OnDestroy,
  LOCALE_ID,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, scan, startWith } from 'rxjs/operators';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { PopupWindowComponent } from '../expert-dialog/popup-window/popup-window.component';
import { PopupWindowStateService } from '../expert-dialog/popup-window/popup-window-state.service';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { UserDetailContextService } from '@app/core/layout/user-detail-context.service';
import { FeatureAttribute, isFeatureAttribute } from '@app/data/models/common/feature.model';

interface GostHeaderTab {
  readonly path: 'overview' | 'progress' | 'records/new_gost';
  readonly label: string;
}

/**
 * Layout wrapper for all user detail sub-pages.
 * Renders a sticky patient context header (desktop/tablet)
 * or M3 fixed header + bottom tab bar (mobile).
 * Contains <router-outlet> for child routes.
 */
@Component({
  selector: 'app-user-detail-layout',
  templateUrl: './user-detail-layout.component.html',
  styleUrls: ['./user-detail-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, RouterOutlet, RouterLink, RouterLinkActive, PopupWindowComponent],
  host: {
    style: 'display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden;',
  },
})
export class UserDetailLayoutComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cacheService = inject(DatabaseCacheService);
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);
  private readonly userDetailContext = inject(UserDetailContextService);
  private readonly localeId = inject(LOCALE_ID);
  private readonly isEnglish = (this.localeId || '').toLowerCase().startsWith('en');
  readonly popupWindowState = inject(PopupWindowStateService);

  private readonly labelSummary = $localize`:@@home.userDetailLayout.tab.summary:サマリー`;
  private readonly labelRecords = $localize`:@@home.userDetailLayout.tab.records:レコード`;
  private readonly labelDialog = $localize`:@@home.userDetailLayout.tab.dialog:連絡帳`;
  private readonly labelVideo = $localize`:@@home.userDetailLayout.tab.video:メディア`;

  private readonly breadcrumbUsers = $localize`:@@home.userDetailLayout.breadcrumb.users:ユーザー`;
  private readonly breadcrumbRecords = $localize`:@@home.userDetailLayout.breadcrumb.records:レコード`;
  private readonly breadcrumbMedia = $localize`:@@home.userDetailLayout.breadcrumb.media:メディア`;
  private readonly breadcrumbDialog = $localize`:@@home.userDetailLayout.breadcrumb.dialog:連絡帳`;
  private readonly breadcrumbGostReport = $localize`:@@home.userDetailLayout.breadcrumb.gostReport:GOSTレポート`;
  private readonly breadcrumbGostProgress = $localize`:@@home.userDetailLayout.breadcrumb.gostProgress:GOST経過表`;

  private readonly userFallbackName = $localize`:@@home.userDetailLayout.fallback.user:ユーザー`;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly cacheVersion = toSignal(
    this.cacheService.parametersUpdated.pipe(
      startWith(void 0),
      scan((version) => version + 1, 0),
    ),
    { initialValue: 0 },
  );

  /** Back button label: always the destination, never the current page */
  readonly backLabel = computed((): string => {
    const url = this.currentUrl();

    // Media detail
    if (/\/medias\/[^/]+/.test(url.split('?')[0])) {
      const from = this.router.parseUrl(url).queryParams['from'] as string | undefined;
      if (from === 'records') return this.breadcrumbRecords;
      if (from === 'dialogs') return this.breadcrumbDialog;
      return this.breadcrumbMedia;
    }

    // GOST record detail
    if (/\/gost\/records\/(?!new_gost(?:\/|$))[^/]+(?:\/|$)/.test(url)) {
      const from = this.router.parseUrl(url).queryParams['from'] as string | undefined;
      return from === 'overview' ? this.breadcrumbGostReport : this.breadcrumbGostProgress;
    }

    // Regular record detail
    if (/\/records\/[^/]+/.test(url)) {
      const from = this.router.parseUrl(url).queryParams['from'] as string | undefined;
      if (from === 'medias') return this.breadcrumbMedia;
      if (from === 'dialogs') return this.breadcrumbDialog;
      return this.breadcrumbRecords;
    }

    return this.breadcrumbUsers;
  });

  /** Feature display order for navigation */
  private readonly functionsOrder: FeatureAttribute[] = [
    'UserData',
    'RecordList',
    'Dialog',
    'Video',
    'GOST',
  ];

  /** Is handset device */
  readonly isHandset = this.layoutBreakpoint.isHandset;
  readonly sexLanguage = this.isEnglish ? 'en' : 'jp';
  readonly detailNavAriaLabel = $localize`:@@home.userDetailLayout.aria.detailNav:ユーザー詳細ナビゲーション`;

  /** Current section ID from route */
  readonly sectionId = computed(() => {
    this.cacheVersion();
    return this.cacheService.getSelectedSection()?.sectionId ?? null;
  });

  /** Current user ID from route */
  readonly userId = computed(() => {
    this.cacheVersion();
    return this.cacheService.getSelectedUserId() ?? null;
  });

  /** Current user info from cache */
  readonly currentUser = computed(() => {
    this.cacheVersion();
    const cachedUser = this.cacheService.getSelectedUserWithUserData();
    if (!cachedUser?.user) return null;

    const user = cachedUser.user;
    const fullName = `${user.lastName || ''} ${user.firstName || ''}`.trim();
    return {
      id: user.userId,
      name: fullName || user.code || this.userFallbackName,
      userName: user.userName || fullName || user.code || this.userFallbackName,
      sex: user.sex,
      birthDate: user.birthDate || '',
    };
  });

  /** Displayed features for navigation tabs */
  readonly displayedFeatures = computed<FeatureAttribute[]>(() => {
    this.cacheVersion();
    const sectionGroup = this.cacheService.getSelectedSectionGroup();
    const functions = sectionGroup?.settings?.functionAttributes;
    if (!Array.isArray(functions)) return [];
    const validFeatures = functions.filter(isFeatureAttribute);
    return this.functionsOrder.filter((f) => validFeatures.includes(f));
  });

  readonly gostHeaderTabs: GostHeaderTab[] = [
    {
      path: 'overview',
      label: $localize`:@@home.gostWorkspace.tab.overview:レポート`,
    },
    {
      path: 'progress',
      label: $localize`:@@home.gostWorkspace.tab.matrix:経過表`,
    },
    {
      path: 'records/new_gost',
      label: $localize`:@@home.gostWorkspace.tab.manual:手入力`,
    },
  ];

  readonly gostSubmenuAriaLabel = $localize`:@@home.userDetailLayout.aria.gostSubmenu:GOSTサブメニュー`;


  ngOnInit(): void {
    // Activate user detail context
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const userId = params['userId'];
        const sectionId = this.route.parent?.snapshot.paramMap.get('sectionId') ?? '';
        if (userId && sectionId) {
          this.userDetailContext.activate(userId, sectionId);
        }
      });

    // Hide popup when navigating away from detail pages
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.isRecordDetail() && !this.isGostRecordDetail() && !this.isMediaDetail()) {
        this.popupWindowState.hide();
      }
    });
  }

  ngOnDestroy(): void {
    this.userDetailContext.deactivate();
    this.popupWindowState.reset();
  }


  /** Whether the current route is a record detail page */
  protected isRecordDetail(): boolean {
    // Check if URL matches /records/:recordId pattern (not just /records)
    const url = this.router.url;
    return /\/records\/[^/]+/.test(url);
  }

  /** Whether the current route is a media detail page (/medias/:mediaId) */
  protected isMediaDetail(): boolean {
    const url = this.router.url.split('?')[0];
    return /\/medias\/[^/]+/.test(url);
  }

  protected isGostRecordDetail(): boolean {
    const url = this.router.url;
    return /\/gost\/records\/(?!new_gost(?:\/|$))[^/]+(?:\/|$)/.test(url);
  }

  private getQueryParam(key: string): string | null {
    return this.router.parseUrl(this.router.url).queryParams[key] ?? null;
  }

  private getGostDetailSourceTab(): 'overview' | 'progress' {
    const from = this.getQueryParam('from');
    return from === 'overview' ? 'overview' : 'progress';
  }

  /** Where did the user navigate from when on media detail? */
  private getMediaDetailSource(): 'records' | 'medias' | 'dialogs' {
    const from = this.getQueryParam('from');
    if (from === 'records') {
      return 'records';
    }
    if (from === 'dialogs') {
      return 'dialogs';
    }
    return 'medias';
  }

  /** Where did the user navigate from when on record detail? */
  private getRecordDetailSource(): 'records' | 'medias' | 'dialogs' {
    const from = this.getQueryParam('from');
    if (from === 'medias') {
      return 'medias';
    }
    if (from === 'dialogs') {
      return 'dialogs';
    }
    return 'records';
  }

  /** Navigate back: detail pages → their parent list, otherwise → user list */
  navigateBack(): void {
    const sectionId = this.sectionId();
    if (!sectionId) return;

    const userId = this.userId();

    // Media detail → records or medias list
    if (this.isMediaDetail() && userId) {
      const source = this.getMediaDetailSource();
      const target = source === 'records'
        ? 'records'
        : source === 'dialogs'
          ? 'dialogs'
          : 'medias';
      this.router.navigate(['/home', sectionId, 'users', userId, target]);
      return;
    }

    // Record detail → record list
    if (this.isRecordDetail() && userId) {
      if (this.isGostRecordDetail()) {
        const target = this.getGostDetailSourceTab();
        this.router.navigate(['/home', sectionId, 'users', userId, 'gost', target]);
        return;
      }
      const source = this.getRecordDetailSource();
      const target = source === 'medias'
        ? 'medias'
        : source === 'dialogs'
          ? 'dialogs'
          : 'records';
      this.router.navigate(['/home', sectionId, 'users', userId, target]);
      return;
    }

    this.router.navigate(['/home', sectionId, 'users']);
  }

  /** Get route path for a feature */
  getFeatureRoute(feature: FeatureAttribute): string[] {
    const sectionId = this.sectionId();
    const userId = this.userId();
    if (!sectionId || !userId) return [];

    const pathMap: Record<string, string> = {
      'UserData': 'summaries',
      'RecordList': 'records',
      'Dialog': 'dialogs',
      'Video': 'medias',
      'GOST': 'gost',
    };
    return ['/home', sectionId, 'users', userId, pathMap[feature] || 'summaries'];
  }

  isGostSectionRoute(): boolean {
    return /\/gost(?:\/|$)/.test(this.router.url);
  }

  getGostHeaderRoute(path: GostHeaderTab['path']): string[] {
    const sectionId = this.sectionId();
    const userId = this.userId();
    if (!sectionId || !userId) return [];
    return ['/home', sectionId, 'users', userId, 'gost', ...path.split('/')];
  }

  isGostHeaderTabActive(path: GostHeaderTab['path']): boolean {
    const url = this.router.url;
    const isOverview = /\/gost\/overview(?:\/|$)/.test(url);
    const isProgress = /\/gost\/progress(?:\/|$)/.test(url);
    const isDetail = this.isGostRecordDetail();
    const detailSource = this.getGostDetailSourceTab();
    const isManual = /\/gost\/records\/new_gost(?:\/|$)/.test(url) || /\/gost\/manual(?:\/|$)/.test(url);

    switch (path) {
      case 'overview':
        return isOverview || (isDetail && detailSource === 'overview');
      case 'progress':
        return isProgress || (isDetail && detailSource === 'progress');
      case 'records/new_gost':
        return isManual;
      default:
        return false;
    }
  }

  /** Get icon for a feature */
  getFeatureIcon(feature: FeatureAttribute): string {
    const iconMap: Record<string, string> = {
      'UserData': 'summarize',
      'RecordList': 'list_alt',
      'Dialog': 'chat',
      'Video': 'videocam',
      'GOST': 'assessment',
    };
    return iconMap[feature] || 'article';
  }

  /** Get label for a feature */
  getFeatureLabel(feature: FeatureAttribute): string {
    const labelMap: Record<string, string> = {
      'UserData': this.labelSummary,
      'RecordList': this.labelRecords,
      'Dialog': this.labelDialog,
      'Video': this.labelVideo,
      'GOST': 'GOST',
    };
    return labelMap[feature] || feature;
  }

  formatAge(age: string): string {
    if (!age || age === '--') {
      return '--';
    }
    return $localize`:@@home.userDetailLayout.age.format:${age}:age:歳`;
  }

}
